import HeartRobot from 'assets/robots/robot-heart.svg';
import { EdithCardLayout } from 'backend/services/types/edithService.types';
import ProductListButtonContainer from 'components/ProductList/Buttons/ProductListButtonContainer';
import ProductListRemoveButton from 'components/ProductList/Buttons/ProductListRemoveButton';
import ProductListToggleButton from 'components/ProductList/Buttons/ProductListToggleButton';
import Header from 'components/ProductList/Header/Header';
import ProductList from 'components/ProductList/ProductList';
import ProductListCards from 'components/ProductList/ProductListCards/ProductListCards';
import Skeleton from 'components/ProductList/Skeleton/Skeleton';
import { Age, ProductListLayout, SearchResponseType } from 'frontend/enums';
import Api from 'frontend/lib/Api';
import { BlockTracking, Product, Program } from 'frontend/types';
import { byIdInCustomOrder } from 'frontend/utils/helpers';
import { useEffect, useState } from 'react';
import styles from './MyList.module.css';
import useSaveToList from 'frontend/utils/hooks/useSaveToList';
import { usePathname } from 'next/navigation';
import { ContinuePlayingProgram } from 'components/ContinuePlaying/ContinuePlaying';
const Fallback = () => <div className={styles.noProgramsSaved} data-sentry-component="Fallback" data-sentry-source-file="MyList.tsx">
    <HeartRobot className={styles.robot} data-sentry-element="HeartRobot" data-sentry-source-file="MyList.tsx" />
    <p className={styles.noProgramsText}>
      Du har ännu inte sparat några program
    </p>
  </div>;
const ProductListContent = ({
  isEditing,
  layout,
  products,
  removeCardDraft,
  setRemoveCardDraft,
  blockTracking
}: {
  isEditing: boolean;
  layout: ProductListLayout;
  products: (Product | ContinuePlayingProgram)[];
  removeCardDraft: number[];
  setRemoveCardDraft: React.Dispatch<React.SetStateAction<number[]>> | null;
  blockTracking: BlockTracking;
}) => <ProductListCards headingLevel={3} animateGridCards cardLayout={EdithCardLayout.DEFAULT} isEditing={isEditing} showAllCards={layout === ProductListLayout.SHOW_MORE} products={layout === ProductListLayout.SHOW_LESS ? products.slice(0, 5) : products} removeCardDraft={removeCardDraft} setRemoveCardDraft={setRemoveCardDraft} blockTracking={blockTracking} data-sentry-element="ProductListCards" data-sentry-component="ProductListContent" data-sentry-source-file="MyList.tsx" />;
interface Props {
  title: string;
  blockTracking: BlockTracking;
  showRobotIfNoSavedProducts: boolean;
}
const MyList = ({
  blockTracking,
  title,
  showRobotIfNoSavedProducts
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    setList,
    ids
  } = useSaveToList();
  const [products, setProducts] = useState<Program[]>([]);
  const [noResultsReceived, setNoResultsReceived] = useState(false);
  const [removeCardDraft, setRemoveCardDraft] = useState<number[]>([]);
  const [layout, setLayout] = useState(ProductListLayout.SHOW_LESS);
  const pathname = usePathname();
  useEffect(() => {
    if (ids.length === 0) return;
    const fetchData = async () => {
      const stringifiedProgramIds = ids.map(id => id.toString());
      const isOnChildPage = pathname === '/barn';
      const productsResponse = await Api.getSearchResults({
        id: stringifiedProgramIds,
        response_type: SearchResponseType.LIMITED,
        ...(isOnChildPage && {
          typical_age_range: [Age.PRESCHOOL, Age.PRIMARY03, Age.PRIMARY46, Age.SPECIALSCHOOLPRIMARY03, Age.SPECIALSCHOOLPRIMARY46]
        })
      });
      if (productsResponse) {
        const sortedResults = productsResponse.results.sort(byIdInCustomOrder(stringifiedProgramIds));
        setProducts(sortedResults as Program[]);
      } else {
        setNoResultsReceived(true);
      }
    };
    fetchData();
  }, [ids.length]);
  const isFallBack = ids.length === 0 || noResultsReceived;
  const handleEndEditing = () => {
    const nextProducts = products.filter(p => !removeCardDraft.includes(p.id));
    setProducts(nextProducts);
    setList(nextProducts.map(p => p.id));
    setIsEditing(false);
    setRemoveCardDraft([]);
  };
  const handleStartEditing = () => setIsEditing(true);
  if (products.length === 0 && !showRobotIfNoSavedProducts) {
    return null;
  }
  return <ProductList data-sentry-element="ProductList" data-sentry-component="MyList" data-sentry-source-file="MyList.tsx">
      <Header title={title} data-sentry-element="Header" data-sentry-source-file="MyList.tsx">
        {!isFallBack && <ProductListButtonContainer>
            <ProductListRemoveButton isEditing={isEditing} onClick={isEditing ? handleEndEditing : handleStartEditing} />
            <ProductListToggleButton numProducts={products.length} setLayout={setLayout} layout={layout} />
          </ProductListButtonContainer>}
      </Header>

      {isFallBack ? <Fallback /> : products.length > 0 ? <ProductListContent isEditing={isEditing} layout={layout} products={products} removeCardDraft={removeCardDraft} setRemoveCardDraft={setRemoveCardDraft} blockTracking={blockTracking} /> : <Skeleton />}
    </ProductList>;
};
export default MyList;